import MemberSelect from '@editor/components/PricingPlan/MemberSelect/MemberSelect';
import {
  FlexColumn,
  FlexContainer,
  FlexRow,
} from '@editor/components/utilities/layouts';
import DefaultSpinner from '@editor/components/utilities/Spinner/Spinner';
import { FREE_TRIAL_DAYS_AMOUNT } from '@editor/global/constants';
import useFreeTrial from '@editor/hooks/useFreeTrial';
import {
  planSelectionStoreSelector,
  usePlanSelectionStore,
} from '@editor/stores/planSelectionStore';
import { userStoreSelector, useUserStore } from '@editor/stores/userStore';
import { formatMonetaryValue } from '@editor/utils/billing';
import { isMobileOperatingSystem } from '@repo/detection';
import { SubscriptionPlanType } from '@repo/subscription/api';
import {
  subscriptionStoreSelector,
  useSubscriptionStore,
} from '@repo/subscription/client';
import { H3, H4, P2, P3 } from '@repo/ui/components/Typography';
import { themeStyle, useNewThemeActive } from '@repo/ui/theming';

import ChangeSubscriptionActions from '../../ActionButtons/ChangeSubscriptionActions/ChangeSubscriptionActions';
import ProceedToCheckoutButton from '../../ActionButtons/CreateSubscriptionActions/ProceedToCheckoutButton';
import useNextCharge from '../../hooks/useNextCharge';
import useSubscriptionChangeState from '../../hooks/useSubscriptionChangeState';
import useSubscriptionPreview from '../../hooks/useSubscriptionPreview';
import CardSection from '../../PaymentInfo/CardSection';
import NextPaymentSection from '../../PaymentInfo/NextPaymentSection';
import { PaymentClarification } from '../../PaymentInfo/PaymentClarification';
import PromoCodeSection from '../../PaymentInfo/PromoCodeSection';
import usePlanPurchaseDetails from '../hooks/usePlanPurchaseDetails';
import PlanIntervalSelector from '../PlanIntervalSelector/PlanIntervalSelector';

import NonTrialRightSection from './NonTrialRightSection';
import { LeftColumnInterval, LeftColumnPayment, TeamComponent } from './styles';
import TrialRightSection from './TrialRightSection';
import { VariantProps } from './types';

/**
 * Checkout flow (variant A)
 *
 * Variant A: will show only the next available plan (PRO for FREE) or the one that the user has explicitly selected (PRO or EXPERT)
 *
 */
const VariantA: React.FC<VariantProps> = ({ onPaymentInit }) => {
  const currentSubscription = useSubscriptionStore(
    subscriptionStoreSelector.subscription
  );

  // Get the subscription change preview data
  const subscriptionChangePreviewData = useSubscriptionPreview({
    currentSubscription,
  });

  // Check if any of the parameters of the subscription have changed
  const isSubscriptionChanged = !!useSubscriptionChangeState({
    currentSubscription,
  });
  const { preview: changeSubscriptionPreview, isLoading: previewIsLoading } =
    subscriptionChangePreviewData;

  // Get the next charge details
  const nextCharge = useNextCharge({
    currentSubscription,
    changeSubscriptionPreview,
  });

  // Get the selected plan, free trial status, team status, and quantity from the respective stores
  const plan = usePlanSelectionStore(planSelectionStoreSelector.plan);
  const freeTrial = useFreeTrial();
  const isTeam = useUserStore(userStoreSelector.isTeam);
  const quantity = usePlanSelectionStore(planSelectionStoreSelector.quantity);

  // Get the title and upgrade status for the plan purchase details
  const { title, isUpgrade } = usePlanPurchaseDetails({
    currentSubscription,
    subscriptionChangePreviewData,
  });

  // Get the action to set the members quantity
  const { setMembersQuantity } = usePlanSelectionStore(
    planSelectionStoreSelector.actions
  );

  // The select component to choose the number of team members to subscribe for
  const memberSelect = isTeam ? (
    <MemberSelect
      selectedNumberOfMembers={quantity}
      onSelect={(id): void => {
        setMembersQuantity(id);
      }}
    />
  ) : null;

  const isNewThemeActive = useNewThemeActive();

  // Free trial layout
  if (freeTrial?.isAvailable) {
    return (
      <FlexContainer height="100%">
        <FlexColumn
          data-testid="subscriptionModal--variant-a-left-section"
          height="100%"
          width={isMobileOperatingSystem() ? '100%' : 'unset'}
        >
          {/* Displays the monthly and yearly options */}
          <LeftColumnInterval>
            <FlexColumn gap="12px">
              <H3>
                <b>
                  Try {plan === SubscriptionPlanType.PRO ? 'Pro' : 'Expert'} For
                  Free
                </b>
              </H3>
              <P2>
                {FREE_TRIAL_DAYS_AMOUNT} days for free, cancel any moment.
              </P2>
            </FlexColumn>
            <FlexColumn gap="12px">
              <PlanIntervalSelector
                isStacked
                currentSubscription={currentSubscription}
              />
            </FlexColumn>
            {isTeam && <TeamComponent>{memberSelect}</TeamComponent>}
          </LeftColumnInterval>

          {/* Displays the payment section */}
          <LeftColumnPayment>
            {nextCharge && (
              <FlexColumn gap="16px">
                <FlexRow justifyContent="space-between">
                  <P3 color={themeStyle.varInkMedium}>{nextCharge.label}</P3>
                  <P3 color={themeStyle.varInkMedium}>
                    {formatMonetaryValue(nextCharge.value)}
                  </P3>
                </FlexRow>
                <FlexRow justifyContent="space-between">
                  <P3>
                    {nextCharge.note?.title} ({FREE_TRIAL_DAYS_AMOUNT} days
                    free)
                  </P3>
                  <H4>
                    {formatMonetaryValue(
                      {
                        ...nextCharge.value,
                        amount: 0,
                      },
                      { fractionDigits: 0 }
                    )}
                  </H4>
                </FlexRow>
              </FlexColumn>
            )}

            {/* Displays "Proceed to Checkout" button if user has no existing subscription, and shows the "Change Subscription" button if otherwise */}
            {currentSubscription?.plan ? (
              <ChangeSubscriptionActions
                showStackedLayout
                currentSubscription={currentSubscription}
                isUpgrade={isUpgrade}
                subscriptionChangePreviewData={subscriptionChangePreviewData}
              />
            ) : (
              <ProceedToCheckoutButton onSuccess={onPaymentInit} />
            )}
          </LeftColumnPayment>
        </FlexColumn>

        {/* Displays information about the Trial */}
        {!isMobileOperatingSystem() && (
          <TrialRightSection
            plan={plan}
            startDate={nextCharge?.date || new Date()}
          />
        )}
      </FlexContainer>
    );
  }

  // Non-trial layout
  return (
    <FlexContainer height="100%">
      <FlexColumn
        data-testid="subscriptionModal--variant-a-left-section"
        height="100%"
        width={isMobileOperatingSystem() ? '100%' : 'unset'}
      >
        {/* Displays the monthly and yearly options */}
        <LeftColumnInterval>
          <H3>
            <b>{title}</b>
          </H3>
          <FlexColumn gap="12px">
            <PlanIntervalSelector
              isStacked
              currentSubscription={currentSubscription}
            />
          </FlexColumn>
          {isTeam && <TeamComponent>{memberSelect}</TeamComponent>}
        </LeftColumnInterval>

        {/* Displays payment section */}
        <LeftColumnPayment>
          {isSubscriptionChanged && previewIsLoading && (
            <DefaultSpinner
              size="24px"
              style={{ paddingBottom: '16px' }}
              theme={{
                color: isNewThemeActive
                  ? themeStyle.varBlueMain
                  : themeStyle.brandMain,
                trackColor: themeStyle.varInkLight,
                borderWidth: '4px',
              }}
            />
          )}
          {!(isSubscriptionChanged && previewIsLoading) &&
            currentSubscription &&
            nextCharge && (
              <>
                <PromoCodeSection nextCharge={nextCharge} />
                <PaymentClarification nextCharge={nextCharge} />
                <NextPaymentSection nextCharge={nextCharge} />
              </>
            )}
          {currentSubscription?.card && !previewIsLoading && (
            <FlexColumn style={{ paddingBottom: '16px' }}>
              <CardSection card={currentSubscription.card} />
            </FlexColumn>
          )}
          {/* Displays "Proceed to Checkout" button if user has no existing subscription, and shows the "Change Subscription" button if otherwise */}
          {currentSubscription?.plan ? (
            <ChangeSubscriptionActions
              showStackedLayout
              currentSubscription={currentSubscription}
              isUpgrade={isUpgrade}
              subscriptionChangePreviewData={subscriptionChangePreviewData}
            />
          ) : (
            <ProceedToCheckoutButton
              disabled={isSubscriptionChanged && previewIsLoading}
              onSuccess={onPaymentInit}
            />
          )}
        </LeftColumnPayment>
      </FlexColumn>
      {!isMobileOperatingSystem() && <NonTrialRightSection plan={plan} />}
    </FlexContainer>
  );
};

export default VariantA;
